import {DatePicker, Radio} from 'antd'
import {course_getCourseClasses_Api, course_getCourses_Api, course_getCourseSubjects_Api,} from 'apis'
import {getId} from 'apis/model/base'
import classNames from 'classnames'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo, useState} from 'react'
import {IoCalendarOutline} from 'react-icons/io5'
import {branch, compose, renderNothing} from 'recompose'
import AntdConfigWrapper, {RenderEmptyMeta,} from 'views/AppLocals/custom/AntdConfigWrapper'
import {reactSelectComponents, SelectWidget,} from 'views/AppLocals/custom/ReactSelectComponents'
import {bindQueryParam, bindQueryParams,} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems, NullAsDefault} from 'views/Shared'
import dayjs from "dayjs";

const FilterSelectField = ({
                             name,
                             value,
                             onChange,
                             options,
                             isLoading,
                             className,
                             ...props
                           }) => (
  <AntdConfigWrapper renderEmpty={RenderEmptyMeta}>
    <SelectWidget
      value={_.find(options, {value}) ? value : undefined}
      showSearch={true}
      allowClear={true}
      onChange={(value) => {
        onChange(name, value)
      }}
      loading={!!isLoading}
      optionFilterProp="label"
      className={classNames('CenterSelector w-full', className)}
      options={!!isLoading ? [] : options}
      suffixIcon={
        !!isLoading ? undefined : <reactSelectComponents.DropdownIndicator/>
      }
      {...props}
    />
  </AntdConfigWrapper>
)

const CourseAsyncWrapper = compose(
  branch(
    ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
    renderNothing
  )
)(({owner_id, owner_type, children}) => {
  const [values, setValues] = useState()

  const apiInfo = course_getCourses_Api

  const cacheId = useMemo(
    () =>
      [
        apiInfo.path.replace(':id', owner_id).replace(':type', owner_type),
        JSON.stringify(values),
      ].join('/'),
    [values, owner_id, owner_type]
  )

  const {response, isLoading} = useAsyncWithCache({
    values,
    cacheId,
    apiInfo,
    query: bindQueryParams([{id: owner_id}, {type: owner_type}]),
  })

  return _.isFunction(children)
    ? children({
      isLoading,
      items: getResponseItems(response),
      onSearch: (keyword) => {
        setValues({
          keyword,
        })
      },
    })
    : children
})

const CourseClassAsyncWrapper = compose(
  branch(({course_id}) => _.isEmpty(course_id), renderNothing)
)(({course_id, children}) => {
  const [values, setValues] = useState()

  const apiInfo = course_getCourseClasses_Api

  const cacheId = useMemo(
    () =>
      [apiInfo.path.replace(':id', course_id), JSON.stringify(values)].join(
        '/'
      ),
    [values, course_id]
  )

  const {response, isLoading} = useAsyncWithCache({
    values,
    cacheId,
    apiInfo,
    query: bindQueryParam({id: course_id}),
  })

  return _.isFunction(children)
    ? children({
      isLoading,
      items: getResponseItems(response),
      onSearch: (keyword) => {
        setValues({
          keyword,
        })
      },
    })
    : children
})

const SubjectAsyncWrapper = compose(
  branch(({course_id}) => !course_id, renderNothing)
)(({course_id, children}) => {
  const [values, setValues] = useState()

  const apiInfo = course_getCourseSubjects_Api
  const cacheId = useMemo(
    () =>
      [apiInfo.path.replace(':id', course_id), JSON.stringify(values)].join(
        '/'
      ),
    [values, course_id]
  )

  const {response, isLoading} = useAsyncWithCache({
    values,
    cacheId,
    apiInfo,
    query: bindQueryParam({id: course_id}),
  })

  return _.isFunction(children)
    ? children({
      isLoading,
      items: getResponseItems(response),
      onSearch: (keyword) => {
        setValues({
          keyword,
        })
      },
    })
    : children
})

export const status_options = {
  pending: {
    value: 1,
    label: 'pending',
  },
  expired: {
    value: 9,
    label: 'expired',
  },
}

export const CouseClassTimetableFilter = ({
                                            owner_id,
                                            owner_type,
                                            defaultValue,
                                            value: filter = {
                                              status: 1,
                                              course_id: undefined,
                                              subject_id: undefined,
                                              course_class_id: undefined,
                                              to_time_open: undefined,
                                              from_time_open: undefined,
                                            },
                                            onChange: setValues = NullAsDefault,
                                          }) => {
  const t = useTranslate()

  const handleChange = useCallback(
    (name, value) => {
      const params = {
        ...filter,
        [name]: value,
      }
      setValues(params)
      return params
    },
    [filter]
  )

  return (
    <div className="flex flex-col items-center gap-2 mt-2">
        <Radio.Group
            name="status"
            optionType="button"
            buttonStyle="solid"
            className="flex items-center justify-end custom-radio-button w-full lg:w-auto"
            onChange={(event) => {
                const {name, value} = event.target
                handleChange(name, value)
            }}
            value={filter.status || undefined}>
            <Radio
                className="px-3 text-sm"
                value={undefined}>
                {t('all')}
            </Radio>
            {[status_options.pending, status_options.expired].map(
                ({label, value}) => (
                    <Radio
                        key={value}
                        className="px-3 text-sm"
                        value={String(value)}>
                        {t(label)}
                    </Radio>
                )
            )}
        </Radio.Group>
        <React.Fragment>
            <CourseAsyncWrapper
                {...{
                    owner_id,
                    owner_type,
                }}>
                {({items, isLoading}) => {
                    const options = Array.from(items).map((option) => ({
                        value: getId(option),
                        label: getTitle(option),
                    }))
                    return (
                        <div className="flex items-center gap-2 w-full">
                <span className="text-sm text-color-400 italic whitespace-no-wrap">
                  {`${t('course')} : `}
                </span>
                            <FilterSelectField
                                name="course_id"
                                loading={isLoading}
                                value={filter.course_id}
                                placeholder={t(isLoading ? 'loading...' : 'course')}
                                options={options}
                                onChange={(name, value) => {
                                    const next = handleChange(name, value)
                                    setValues({
                                        ...(next ?? {}),
                                        subject_id: undefined,
                                        course_class_id: undefined,
                                    })
                                }}
                            />
                        </div>
                    )
                }}
            </CourseAsyncWrapper>

            <SubjectAsyncWrapper {...{course_id: filter.course_id}}>
                {({items, isLoading}) => {
                    const options = Array.from(items).map((option) => {
                        const subject = option?.subject
                        return {
                            value: getId(subject),
                            label: getTitle(subject),
                        }
                    })
                    return (
                        <div className="flex items-center gap-2 w-full">
                <span className="text-sm text-color-400 italic whitespace-no-wrap">
                  {`${t('subjects')} : `}
                </span>
                            <FilterSelectField
                                name="subject_id"
                                options={options}
                                loading={isLoading}
                                value={filter.subject_id}
                                placeholder={t(isLoading ? 'loading...' : 'subjects')}
                                onChange={(name, value) => {
                                    handleChange(name, value)
                                }}
                            />
                        </div>
                    )
                }}
            </SubjectAsyncWrapper>

            <CourseClassAsyncWrapper {...{course_id: filter.course_id}}>
                {({items, isLoading}) => {
                    const options = Array.from(items).map((option) => ({
                        value: getId(option),
                        label: getTitle(option),
                    }))
                    return (
                        <div className="flex items-center gap-2 w-full">
                <span className="text-sm text-color-400 italic whitespace-no-wrap">
                  {`${t('classes')} : `}
                </span>
                            <FilterSelectField
                                loading={isLoading}
                                name="course_class_id"
                                value={filter.course_class_id}
                                placeholder={t(isLoading ? 'loading...' : 'classes')}
                                options={options}
                                onChange={(name, value) => {
                                    handleChange(name, value)
                                }}
                            />
                        </div>
                    )
                }}
            </CourseClassAsyncWrapper>
        </React.Fragment>
        <div className="flex items-center gap-2 w-full">
            <span className="text-sm text-color-400 italic whitespace-no-wrap">
                {`${t('time')} : `}
            </span>
            <div className="flex w-full items-center gap-2 justify-end grid grid-cols-1 md:grid-cols-2">
                <DatePicker
                    allowClear={true}
                    className="w-full"
                    suffixIcon={<IoCalendarOutline className="text-color-500"/>}
                    onChange={(date, dateString) => {
                        handleChange('from_time_open', dateString)
                    }}
                    format="DD/MM/YYYY"
                    defaultValue={
                        filter.from_time_open
                            ? dayjs(new Date(filter.from_time_open))
                            : undefined
                    }
                    placeholder={t('from date')}
                />
                <DatePicker
                    allowClear={true}
                    className="w-full"
                    suffixIcon={<IoCalendarOutline className="text-color-500"/>}
                    onChange={(date, dateString) => {
                        handleChange('to_time_open', dateString)
                    }}
                    format="DD/MM/YYYY"
                    defaultValue={
                        filter.to_time_open
                            ? dayjs(new Date(filter.to_time_open))
                            : undefined
                    }
                    placeholder={t('to date')}
                />
            </div>
        </div>
    </div>
  )
}

export default CouseClassTimetableFilter
